import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

const PartOrderDialog = ({ open, onClose, selectedItems, locationData, onOrderSubmit }) => {
  const [orderData, setOrderData] = useState({
    soldTo: locationData?.customer_name || '',
    shipTo: locationData?.name || '',
    supplier: '',
    requestDate: new Date().toISOString().split('T')[0],
    orderDetails: []
  });

  // Hard-coded suppliers with their inventory data
  const suppliers = [
    {
      id: 'jci_main',
      name: 'JCI Main Distribution',
      available: 85,
      inTransit: 39,
      ordered: 21
    },
    {
      id: 'jci_south',
      name: 'JCI South Region Distribution',
      available: 35,
      inTransit: 16,
      ordered: 11
    },
    {
      id: 'jci_west',
      name: 'JCI West Region Distribution',
      available: 21,
      inTransit: 4,
      ordered: 7
    }
  ];

  // Update orderDetails when selectedItems changes
  useEffect(() => {
    if (selectedItems && selectedItems.length > 0) {
      console.log('Selected Items:', selectedItems);
      
      const newOrderDetails = selectedItems.map(item => {
        console.log('Processing item:', item);
        
        // Calculate default order quantity based on target - onsite
        const defaultOrderQuantity = Math.max(0, (item.target_quantity || 0) - (item.total_quantity || 0));
        
        return {
          partNumber: item.sku,
          description: item.description,
          orderQuantity: defaultOrderQuantity,
          onSiteQuantity: item.total_quantity,
          targetQuantity: item.target_quantity || 0
        };
      });

      console.log('New Order Details:', newOrderDetails);

      setOrderData(prev => ({
        ...prev,
        orderDetails: newOrderDetails
      }));
    } else {
      // Reset order details if no items are selected
      setOrderData(prev => ({
        ...prev,
        orderDetails: []
      }));
    }
  }, [selectedItems]);

  const handleQuantityChange = (index, value) => {
    const newOrderDetails = [...orderData.orderDetails];
    newOrderDetails[index].orderQuantity = Math.max(0, parseInt(value) || 0);
    setOrderData(prev => ({
      ...prev,
      orderDetails: newOrderDetails
    }));
  };

  const handleIncrement = (index) => {
    const newOrderDetails = [...orderData.orderDetails];
    newOrderDetails[index].orderQuantity += 1;
    setOrderData(prev => ({
      ...prev,
      orderDetails: newOrderDetails
    }));
  };

  const handleDecrement = (index) => {
    const newOrderDetails = [...orderData.orderDetails];
    newOrderDetails[index].orderQuantity = Math.max(0, newOrderDetails[index].orderQuantity - 1);
    setOrderData(prev => ({
      ...prev,
      orderDetails: newOrderDetails
    }));
  };

  const handleSubmit = () => {
    onOrderSubmit(orderData);
    onClose();
  };

  // Log the current orderData to debug
  console.log('Current Order Data:', orderData);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Part Order Request</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sold To"
                value={orderData.soldTo}
                InputProps={{ readOnly: true }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ship To"
                value={orderData.shipTo}
                InputProps={{ readOnly: true }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={orderData.supplier}
                  onChange={(e) => setOrderData(prev => ({ ...prev, supplier: e.target.value }))}
                  label="Supplier"
                >
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      <Box>
                        <Typography>{supplier.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Available: {supplier.available} ({supplier.inTransit} in transit) ({supplier.ordered} ordered)
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Request Date"
                type="date"
                value={orderData.requestDate}
                onChange={(e) => setOrderData(prev => ({ ...prev, requestDate: e.target.value }))}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Order Details
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="20%">Part Number</TableCell>
                  <TableCell width="45%">Description</TableCell>
                  <TableCell width="15%" align="center">Order Quantity</TableCell>
                  <TableCell width="10%" align="center">On Site Quantity</TableCell>
                  <TableCell width="10%" align="center">Target Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderData.orderDetails && orderData.orderDetails.length > 0 ? (
                  orderData.orderDetails.map((item, index) => (
                    <TableRow key={item.partNumber || index}>
                      <TableCell>{item.partNumber}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={item.orderQuantity}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                          inputProps={{ 
                            min: 0,
                            style: { textAlign: 'center', width: '80px' }
                          }}
                          size="small"
                          sx={{ width: '100px' }}
                        />
                      </TableCell>
                      <TableCell>{item.onSiteQuantity}</TableCell>
                      <TableCell>{item.targetQuantity}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No items selected
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!orderData.supplier || orderData.orderDetails.length === 0}
        >
          Submit Parts Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartOrderDialog; 